import { useEffect, useState } from "react";
import client, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Field, Input, Textarea, Select, Empty } from "@/components/kit";
import { Save, Upload, Fingerprint, Phone } from "lucide-react";

const TONE_OPTIONS = ["Profissional", "Descontraído", "Educativo", "Inspiracional", "Acolhedor"];

export default function DnaMarca() {
  const { activeOrgId } = useAuth();
  const [brands, setBrands] = useState([]);
  const [sel, setSel] = useState("");
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!activeOrgId) return;
    client.get("/brands").then((r) => { setBrands(r.data); if (r.data[0]) setSel(r.data[0].id); });
  }, [activeOrgId]);

  useEffect(() => {
    if (!sel) return;
    let cancelled = false;
    setB(null);
    client.get(`/brands/${sel}`).then((r) => {
      if (!cancelled) setB(r.data);
    });
    return () => { cancelled = true; };
  }, [sel]);

  useEffect(() => {
    if (!b?.logo_media_id) { setLogoUrl(null); return; }
    client.get(`/media/${b.logo_media_id}/file`, { responseType: "blob" })
      .then((r) => setLogoUrl(URL.createObjectURL(r.data))).catch(() => setLogoUrl(null));
  }, [b?.logo_media_id]);

  const dna = b?.dna || {};
  const setDna = (k, v) => setB({ ...b, dna: { ...dna, [k]: v } });
  const toggleTone = (t) => {
    const cur = dna.tone_options || [];
    setDna("tone_options", cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/brands/${sel}`, {
        name: b.name, niche: b.niche, site: b.site, tone: b.tone, color: b.color,
        phone: b.phone || "", email: b.email || "", whatsapp: b.whatsapp || "",
        instagram: b.instagram || "", address: b.address || "",
        mission: dna.mission, audience: dna.audience, keywords: dna.keywords,
        competitors: dna.competitors, tone_options: dna.tone_options,
      });
      toast.success("DNA da Marca salvo no banco");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("category", "Logos"); fd.append("brand_id", sel);
    try {
      const { data } = await client.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await client.put(`/brands/${sel}`, { logo_media_id: data.id });
      setB({ ...b, logo_media_id: data.id });
      toast.success("Logo enviado ao Object Storage");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!brands.length) return <div><PageHeader title="DNA da Marca" /><Empty>Crie uma marca em Empresas primeiro.</Empty></div>;

  return (
    <div>
      <PageHeader title="DNA da Marca" subtitle="A identidade que orienta toda a geração de conteúdo por IA."
        actions={<Btn data-testid="save-dna-btn" onClick={save} disabled={saving}><Save size={16} /> {saving ? "Salvando..." : "Salvar DNA"}</Btn>} />

      <div className="mb-5 max-w-xs">
        <Field label="Marca ativa">
          <Select data-testid="dna-brand-select" value={sel} onChange={(e) => setSel(e.target.value)}>
            {brands.map((br) => <option key={br.id} value={br.id}>{br.name}</option>)}
          </Select>
        </Field>
      </div>

      {b && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome"><Input data-testid="dna-name" value={b.name || ""} onChange={(e) => setB({ ...b, name: e.target.value })} /></Field>
              <Field label="Nicho"><Input data-testid="dna-niche" value={b.niche || ""} onChange={(e) => setB({ ...b, niche: e.target.value })} /></Field>
              <Field label="Site"><Input data-testid="dna-site" value={b.site || ""} onChange={(e) => setB({ ...b, site: e.target.value })} /></Field>
              <Field label="Tom de voz principal"><Input data-testid="dna-tone" value={b.tone || ""} onChange={(e) => setB({ ...b, tone: e.target.value })} /></Field>
            </div>
            <Field label="Missão"><Textarea data-testid="dna-mission" rows={2} value={dna.mission || ""} onChange={(e) => setDna("mission", e.target.value)} /></Field>
            <Field label="Público-alvo"><Textarea data-testid="dna-audience" rows={2} value={dna.audience || ""} onChange={(e) => setDna("audience", e.target.value)} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Palavras-chave"><Input data-testid="dna-keywords" value={dna.keywords || ""} onChange={(e) => setDna("keywords", e.target.value)} /></Field>
              <Field label="Concorrentes"><Input data-testid="dna-competitors" value={dna.competitors || ""} onChange={(e) => setDna("competitors", e.target.value)} /></Field>
            </div>
            <Field label="Tons permitidos">
              <div className="flex flex-wrap gap-2">
                {TONE_OPTIONS.map((t) => {
                  const on = (dna.tone_options || []).includes(t);
                  return <button key={t} type="button" data-testid={`tone-${t}`} onClick={() => toggleTone(t)}
                    className={`rounded-full px-3 py-1.5 text-sm border transition-all ${on ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>;
                })}
              </div>
            </Field>

            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
              <div className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2"><Phone size={15} className="text-[#3457d5]" /> Dados de contato (usados nas artes)</div>
              <p className="text-xs text-slate-500 mb-3">A IA usa <b>somente</b> estes dados reais nas imagens/vídeos e <b>nunca inventa</b>. Deixe em branco o que não quiser exibir.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefone"><Input data-testid="dna-phone" value={b.phone || ""} onChange={(e) => setB({ ...b, phone: e.target.value })} placeholder="(11) 3333-4444" /></Field>
                <Field label="WhatsApp"><Input data-testid="dna-whatsapp" value={b.whatsapp || ""} onChange={(e) => setB({ ...b, whatsapp: e.target.value })} placeholder="(11) 99999-9999" /></Field>
                <Field label="E-mail"><Input data-testid="dna-email" value={b.email || ""} onChange={(e) => setB({ ...b, email: e.target.value })} placeholder="contato@suamarca.com" /></Field>
                <Field label="Instagram"><Input data-testid="dna-instagram" value={b.instagram || ""} onChange={(e) => setB({ ...b, instagram: e.target.value })} placeholder="@suamarca" /></Field>
                <div className="sm:col-span-2"><Field label="Endereço"><Input data-testid="dna-address" value={b.address || ""} onChange={(e) => setB({ ...b, address: e.target.value })} placeholder="Rua, número, cidade" /></Field></div>
              </div>
            </div>
          </Card>

          <Card className="space-y-4">
            <h3 className="font-display font-bold flex items-center gap-2"><Fingerprint size={18} className="text-[#3457d5]" /> Identidade visual</h3>
            <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 flex flex-col items-center gap-3">
              {logoUrl ? <img src={logoUrl} alt="logo" className="h-24 w-24 object-contain rounded-xl" data-testid="brand-logo-preview" />
                : <div className="h-24 w-24 rounded-xl flex items-center justify-center text-white text-2xl font-bold" style={{ background: b.color }}>{(b.name || "?").charAt(0)}</div>}
              <label className="cursor-pointer">
                <span className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="upload-logo-label"><Upload size={15} /> Enviar logo</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="upload-logo-input" />
              </label>
            </div>
            <Field label="Cor da marca"><input type="color" data-testid="dna-color" value={b.color || "#3457d5"} onChange={(e) => setB({ ...b, color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200" /></Field>
            <div className="text-xs text-slate-400">Logos e mídias são armazenados no Object Storage (REAL).</div>
          </Card>
        </div>
      )}
    </div>
  );
}
